import styled from 'styled-components';
import {media} from '../../styles/components/media-queries';

// Chrome (wrapper, step indicator, card, form fields, buttons) copied from
// CreateSignupPage.style.js so this shorter flow matches the referral wizard
// look exactly.

export const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 80rem;
  margin: 4rem auto 4rem;
  padding: 0 2rem;
  ${media.smaller`
    margin: 1rem auto;
    padding: 0 1rem;
  `};
`;

export const PageTitle = styled.h1`
  color: ${p => p.theme.secondaryColor};
  font-size: 3.2rem;
  font-weight: lighter;
  margin-bottom: 3rem;
  ${media.smaller`
    font-size: 2.2rem;
    margin-bottom: 1.5rem;
  `};
`;

// ─── Step Indicator ──────────────────────────────────────────────────────────

export const StepIndicatorWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 3rem;
`;

export const StepItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.6rem;
  flex-shrink: 0;
`;

export const StepDot = styled.div`
  width: 3.6rem;
  height: 3.6rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  font-weight: bold;
  transition: background-color 0.2s, color 0.2s, border-color 0.2s;
  border: 0.2rem solid
    ${p =>
      p.$active
        ? p.theme.secondaryColor
        : p.$done
        ? p.theme.secondaryColor
        : p.theme.borderColor};
  background-color: ${p =>
    p.$active
      ? p.theme.secondaryColor
      : p.$done
      ? 'transparent'
      : 'transparent'};
  color: ${p =>
    p.$active
      ? p.theme.primaryTextColor || '#fff'
      : p.$done
      ? p.theme.secondaryColor
      : p.theme.tertiaryTextColor || '#999'};
`;

export const StepLabel = styled.span`
  font-size: 1.2rem;
  text-align: center;
  color: ${p =>
    p.$active ? p.theme.secondaryColor : p.theme.tertiaryTextColor || '#999'};
  font-weight: ${p => (p.$active ? 'bold' : 'normal')};
  white-space: nowrap;
  ${media.smaller`
    display: none;
  `};
`;

export const StepLine = styled.div`
  flex: 1;
  height: 0.2rem;
  margin: 0 0.8rem;
  margin-bottom: 2.4rem;
  background-color: ${p =>
    p.$done ? p.theme.secondaryColor : p.theme.borderColor};
  transition: background-color 0.2s;
  ${media.smaller`
    margin-bottom: 0;
  `};
`;

// ─── Card ─────────────────────────────────────────────────────────────────────

export const StepCard = styled.div`
  border: 0.1rem solid ${p => p.theme.borderColor};
  border-radius: 0.4rem;
  padding: 2.4rem;
  background-color: ${p => p.theme.backgroundColor || '#fff'};
  position: relative;
`;

export const SectionTitle = styled.h2`
  color: ${p => p.theme.secondaryColor};
  font-size: 2rem;
  font-weight: lighter;
  margin: 0 0 2rem;
`;

// ─── Form Fields ──────────────────────────────────────────────────────────────

export const FormGroup = styled.div`
  margin-bottom: 2rem;
`;

export const Label = styled.label`
  display: block;
  font-size: 1.4rem;
  font-weight: bold;
  color: ${p => p.theme.textColor};
  margin-bottom: 0.6rem;

  span {
    font-weight: normal;
    color: ${p => p.theme.tertiaryTextColor || '#999'};
    margin-left: 0.4rem;
  }
`;

export const Input = styled.input`
  width: 100%;
  padding: 1rem 1.2rem;
  font-size: 1.6rem;
  border: 0.1rem solid ${p => (p.$hasError ? '#d32f2f' : p.theme.borderColor)};
  border-radius: 0.4rem;
  color: ${p => p.theme.textColor};
  background-color: #fff;
  box-sizing: border-box;
  transition: border-color 0.15s;

  &:focus {
    outline: none;
    border-color: ${p => (p.$hasError ? '#d32f2f' : p.theme.secondaryColor)};
  }
`;

export const FieldError = styled.p`
  font-size: 1.3rem;
  color: #d32f2f;
  margin: 0.4rem 0 0;
`;

// ─── Messages ─────────────────────────────────────────────────────────────────

export const ErrorMessage = styled.div`
  margin-top: 1.2rem;
  margin-bottom: 2rem;
  padding: 1.2rem 1.4rem;
  background-color: rgba(211, 47, 47, 0.07);
  border: 0.1rem solid #d32f2f;
  border-radius: 0.4rem;
  font-size: 1.4rem;
  color: #d32f2f;
`;

// ─── Buttons ──────────────────────────────────────────────────────────────────

export const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1.2rem;
  margin-top: 2.4rem;
  flex-wrap: wrap;
`;

export const PrimaryButton = styled.button`
  padding: 1.1rem 2.8rem;
  font-size: 1.5rem;
  font-weight: bold;
  text-transform: uppercase;
  color: ${p => p.theme.primaryTextColor || '#fff'};
  background-color: ${p => p.theme.secondaryColor};
  border: none;
  border-radius: 0.4rem;
  cursor: pointer;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.88;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

export const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  background-color: rgba(255, 255, 255, 0.85);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  border-radius: 0.4rem;

  p {
    font-size: 1.4rem;
    color: ${p => p.theme.secondaryColor};
    font-weight: bold;
  }
`;

// ─── Confirmation step (matches confirm.png — plain centred content, no
// card/border around it) ────────────────────────────────────────────────────

export const ConfirmationWrapper = styled.div`
  text-align: center;
  padding: 2rem 1rem;
`;

export const ConfirmationTitle = styled.h1`
  color: ${p => p.theme.secondaryColor};
  font-size: 3.2rem;
  font-weight: bold;
  margin: 0 0 2rem;
  ${media.smaller`
    font-size: 2.4rem;
  `};
`;

export const ConfirmationText = styled.p`
  font-size: 1.6rem;
  line-height: 2.4rem;
  color: ${p => p.theme.tertiaryTextColor || '#666'};
  margin: 0 0 1.6rem;

  strong {
    color: ${p => p.theme.textColor};
  }
`;
